import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';
import axiosInstance from '../../api/axios';
import DateRangePicker from '../dateRangePicker/DateRangePicker';
import BonusTable, { Bonus } from './BonusTable';
import Pagination from '../pagination/pagination';
import styles from './myBonuses.module.scss';

const MyBonuses: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [bonuses, setBonuses] = useState<Bonus[]>([]);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const fetchBonuses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '10');

      if (fromDate) {
        params.append('dateFrom', fromDate.startOf('day').toISOString());
      }
      if (toDate) {
        params.append('dateTo', toDate.endOf('day').toISOString());
      }

      const response = await axiosInstance.get('/client/bonuses/my-history', { params });
      const { docs, totalPages } = response.data.data;
      setBonuses(docs || []);
      setTotalPages(totalPages || 1);
    } catch (error) {
      console.error('Error fetching bonuses:', error);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, page]);

  useEffect(() => {
    fetchBonuses();
  }, [fetchBonuses]);

  return (
    <div className={styles.container}>
      <div className={styles.filterToggle} onClick={() => setIsFiltersOpen(!isFiltersOpen)}>
        <div className={styles.toggleLeft}>
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="4" y1="6" x2="20" y2="6"></line>
            <line x1="6" y1="12" x2="18" y2="12"></line>
            <line x1="9" y1="18" x2="15" y2="18"></line>
          </svg>
          <span>{t('myBonuses.filters')}</span>
        </div>
        <svg
          className={`${styles.arrow} ${isFiltersOpen ? styles.open : ''}`}
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>

      <div className={`${styles.filters} ${isFiltersOpen ? styles.isOpen : ''}`}>
        <div className={styles.innerFilters}>
          <div className={styles.filterRow}>
            <div className={styles.filterItem}>
              <DateRangePicker
                fromDate={fromDate}
                toDate={toDate}
                onFromChange={(date) => {
                  setFromDate(date);
                  setPage(1);
                }}
                onToChange={(date) => {
                  setToDate(date);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <BonusTable bonuses={bonuses} loading={loading} />

      {!loading && bonuses.length > 0 && (
        <div className={styles.paginationWrapper}>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
};

export default MyBonuses;
